import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Clock, Download, Hourglass, Package, Search, Timer, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useToast } from '@/components/ui/useToast';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatDate, formatINR } from '@/lib/format';
import { listLocations } from '@/api/locations';
import {
  getStockAgeing,
  listStockCollections,
  stockAgeingExportPath,
  NO_COLLECTION,
  type StockAgeingRow,
} from '@/api/warehouseStock';

/** Colour of the On Hand total card. */
const TOTAL_COLOR = '#6366F1';

/** Age buckets, freshest to oldest — colour goes green → red as stock gets older. */
const BUCKETS: readonly { label: string; color: string; Icon: LucideIcon }[] = [
  { label: '0–30 days', color: '#10B981', Icon: Clock },
  { label: '31–60 days', color: '#F59E0B', Icon: Timer },
  { label: '61–90 days', color: '#F97316', Icon: Hourglass },
  { label: '90+ days', color: '#EF4444', Icon: AlertTriangle },
];

const compact = (n: number) =>
  n >= 1_00_00_000 ? `₹${(n / 1_00_00_000).toFixed(2)}Cr`
  : n >= 1_00_000 ? `₹${(n / 1_00_000).toFixed(2)}L`
  : n >= 1_000 ? `₹${(n / 1_000).toFixed(1)}K`
  : `₹${Math.round(n)}`;
const pct = (v: number, total: number) => (total > 0 && v > 0 ? `${((v / total) * 100).toFixed(v / total < 0.1 ? 1 : 0)}%` : '0%');
const pcsText = (n: number) => n.toLocaleString('en-IN');

export function StockAgeingPage() {
  const toast = useToast();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [collection, setCollection] = useState('');
  const [category, setCategory] = useState('');
  const [locationId, setLocationId] = useState<number | ''>('');
  const [bucket, setBucket] = useState<number | ''>(''); // show only items with stock in this bucket
  const [exporting, setExporting] = useState(false);

  // Search runs on the server — wait for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filter = useMemo(
    () => ({
      search: search || undefined,
      collection: collection || undefined,
      category: category || undefined,
      locationId: locationId === '' ? undefined : locationId,
    }),
    [search, collection, category, locationId],
  );
  const q = useQuery({ queryKey: ['stock-ageing', filter], queryFn: () => getStockAgeing(filter), placeholderData: keepPreviousData });
  const collectionsQ = useQuery({ queryKey: ['warehouse-stock-collections'], queryFn: listStockCollections, staleTime: 5 * 60_000 });
  const locationsQ = useQuery({ queryKey: ['locations-flat'], queryFn: () => listLocations({}), staleTime: 60_000 });

  const data = q.data;
  const totals = data?.totals;

  // Category options come from the unfiltered result, so picking one doesn't shrink the list to itself.
  const categoriesRef = useRef<string[]>([]);
  // One entry per category regardless of case ("BEDSHEET" / "Bedsheet") — the filter ignores case anyway.
  if (data && !category) {
    const byKey = new Map<string, string>();
    for (const r of data.rows) if (r.category && !byKey.has(r.category.toLowerCase())) byKey.set(r.category.toLowerCase(), r.category);
    categoriesRef.current = [...byKey.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  const collectionOptions = useMemo(() => [
    { value: '', label: 'All collections' },
    ...(collectionsQ.data?.collections ?? []).map((c) => ({ value: c.name, label: c.name, sublabel: `${c.items} item${c.items === 1 ? '' : 's'}` })),
    ...(collectionsQ.data?.withoutCollection ? [{ value: NO_COLLECTION, label: 'No collection', sublabel: `${collectionsQ.data.withoutCollection} items` }] : []),
  ], [collectionsQ.data]);

  // Bucket filter: items holding stock in that bucket, oldest stock first.
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    if (bucket === '') return all;
    return all.filter((r) => r.buckets[bucket] > 0).sort((a, b) => b.values[bucket] - a.values[bucket]);
  }, [data, bucket]);

  async function exportExcel() {
    setExporting(true);
    try {
      await downloadExcel(stockAgeingExportPath(filter), 'GRHAMOY_StockAgeing.xlsx', 180_000);
    } catch (e) {
      toast.push(apiError(e, 'Export failed'), 'error');
    } finally {
      setExporting(false);
    }
  }

  const bucketCell = (i: number) => (r: StockAgeingRow) =>
    r.buckets[i] > 0
      ? <span className="font-bold tabular-nums" style={{ color: BUCKETS[i].color }} title={`₹${formatINR(r.values[i])}`}>{pcsText(r.buckets[i])}</span>
      : <span className="text-[var(--text-muted)]">—</span>;

  return (
    <div>
      <SectionHeader title="Stock Ageing" />

      {/* Total on hand, then one tile per age bucket — click a bucket to list the items holding stock of that age. */}
      <div className="mb-2 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-5">
        <div
          className="relative col-span-2 overflow-hidden rounded-xl p-3 text-white shadow-sm sm:px-4 sm:py-3 lg:col-span-1"
          style={{ background: TOTAL_COLOR, textShadow: KPI_TEXT_SHADOW }}
        >
          <Package className="absolute -bottom-3 -right-3 h-16 w-16" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
          <div className="relative">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/90">On Hand</span>
            <div className="mt-1 text-xl font-extrabold leading-tight tabular-nums sm:text-2xl" title={totals ? `₹${formatINR(totals.value)} at purchase rate · as of ${formatDate(data!.asOf)}` : undefined}>
              {q.isLoading ? <span className="inline-block h-6 w-20 animate-pulse rounded" style={{ background: 'rgba(255,255,255,0.3)' }} /> : compact(totals?.value ?? 0)}
            </div>
            <div className="truncate text-[10px] font-semibold text-white/85">
              {totals ? `${pcsText(totals.pcs)} pcs · ${totals.items} items` : '—'}
            </div>
            {data && (
              <div className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-bold" style={{ background: 'rgba(255,255,255,0.22)' }}>
                <CalendarClock size={10} className="shrink-0" /> As of {formatDate(data.asOf)}
              </div>
            )}
          </div>
        </div>
        {BUCKETS.map((b, i) => {
          const t = totals?.buckets[i];
          const on = bucket === i;
          return (
            <button
              key={b.label}
              type="button"
              onClick={() => setBucket(on ? '' : i)}
              title={on ? 'Show all items' : `Show only items with stock aged ${b.label}`}
              aria-pressed={on}
              className={`relative flex flex-col overflow-hidden rounded-xl p-3 text-left text-white shadow-sm transition hover:-translate-y-0.5 sm:px-4 sm:py-3 ${on ? 'ring-2 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}
              style={{ background: b.color, textShadow: KPI_TEXT_SHADOW, ...(on ? { ['--tw-ring-color' as string]: b.color } : {}) }}
            >
              <b.Icon className="absolute -bottom-3 -right-3 h-16 w-16" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
              <div className="relative">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/90">{b.label}</span>
                <div className="mt-1 text-xl font-extrabold leading-tight tabular-nums sm:text-2xl">
                  {q.isLoading ? <span className="inline-block h-6 w-20 animate-pulse rounded" style={{ background: 'rgba(255,255,255,0.3)' }} /> : compact(t?.value ?? 0)}
                </div>
                <div className="truncate text-[10px] font-semibold text-white/85">
                  {t ? `${pcsText(t.pcs)} pcs · ${t.items} item${t.items === 1 ? '' : 's'} · ${pct(t.value, totals?.value ?? 0)} of value` : '—'}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:w-[190px]">
          <div className="relative w-full">
            <Search size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search" className="!pl-5 sm:!pl-8" aria-label="Search items" title="Item code, name, serial or barcode" />
          </div>
        </FilterField>
        <FilterField label="Collection" className="sm:w-[210px]">
          <SearchableSelect options={collectionOptions} value={collection} onChange={(v) => setCollection(v === '' ? '' : String(v))} placeholder="All collections" dense />
        </FilterField>
        <FilterField label="Category" className="sm:w-[170px]">
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All</option>
            {categoriesRef.current.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Location" className="sm:w-[178px]">
          <Select value={locationId === '' ? '' : String(locationId)} onChange={(e) => setLocationId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">All locations</option>
            {(locationsQ.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Age" className="sm:w-[140px]">
          <Select value={bucket === '' ? '' : String(bucket)} onChange={(e) => setBucket(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">All ages</option>
            {BUCKETS.map((b, i) => <option key={b.label} value={i}>{b.label}</option>)}
          </Select>
        </FilterField>
        <button
          type="button"
          onClick={exportExcel}
          disabled={exporting || !data || data.rows.length === 0}
          title="Export the ageing of every item matching these filters to Excel"
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent disabled:opacity-60 sm:col-span-1 sm:ml-auto sm:h-8 sm:gap-1.5 sm:self-end sm:px-3 sm:text-xs"
        >
          <Download size={12} /> {exporting ? 'Exporting…' : 'Export'}
        </button>
      </FilterBar>

      <Table<StockAgeingRow>
        paginated
        storageKey="stock-ageing"
        title={bucket === '' ? 'Ageing by item' : `Items with stock aged ${BUCKETS[bucket].label}`}
        count={`${rows.length.toLocaleString('en-IN')} item${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.itemId}
        loading={q.isLoading}
        empty="No stock on hand matches these filters."
        columns={[
          { key: 'img', header: '', cell: (r) => <ItemThumb src={r.imageUrl} size={28} /> },
          { key: 'barcode', header: 'SKU Barcode', cell: (r) => (r.barcode ? <span className="font-extrabold text-accent" title={`Item code: ${r.code}`}>{r.barcode}</span> : <span className="text-[var(--text-muted)]" title={`Item code: ${r.code}`}>—</span>) },
          { key: 'name', header: 'Item Name', cell: (r) => <span className="mx-auto block max-w-[190px] truncate font-bold" title={r.name}>{r.name}</span> },
          { key: 'category', header: 'Category', cell: (r) => r.category ?? '—' },
          { key: 'collection', header: 'Collection', cell: (r) => r.collection ?? '—' },
          { key: 'pcs', header: 'On Hand', align: 'right', cell: (r) => <span className="font-bold tabular-nums">{pcsText(r.pcs)}</span> },
          { key: 'b0', header: '0–30', align: 'right', cell: bucketCell(0) },
          { key: 'b1', header: '31–60', align: 'right', cell: bucketCell(1) },
          { key: 'b2', header: '61–90', align: 'right', cell: bucketCell(2) },
          { key: 'b3', header: '90+', align: 'right', cell: bucketCell(3) },
          { key: 'value', header: 'Value', align: 'right', cell: (r) => <span className="tabular-nums">₹{formatINR(r.value)}</span> },
          {
            key: 'oldest', header: 'Oldest', align: 'right',
            cell: (r) => (
              <span className="tabular-nums" title={`Received ${formatDate(r.oldestReceived)}`}>
                <strong>{r.oldestDays}</strong><span className="text-[var(--text-muted)]"> d</span>
              </span>
            ),
          },
          {
            key: 'actions', header: '', alwaysVisible: true,
            cell: (r) => (
              <Link
                to={`/inventory/wh-ledger?itemId=${r.itemId}`}
                title="Open this item's warehouse ledger"
                className="inline-flex items-center rounded-lg border border-[var(--border-color)] bg-white/[0.04] px-2.5 py-0.5 text-xs font-bold text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent"
              >
                Ledger
              </Link>
            ),
          },
        ]}
      />
    </div>
  );
}
